import { MedicalMatchRerunBlockedReason, LiveMedicalMatchBlockedReason } from '@/api';
import type { CanRunLiveMedicalMatchResponse, MedicalMatchItem, MedicalMatchQuestion } from '@/api';
import {
  getLiveQuestionDuplicateMessage,
  LIVE_MEDICAL_MATCH_BLOCKED_MESSAGE,
  LIVE_MEDICAL_MATCH_CHECK_FAILED_MESSAGE,
  LIVE_MEDICAL_MATCH_CHECKING_MESSAGE,
  LIVE_MEDICAL_MATCH_UNAVAILABLE_MESSAGE,
  LIVE_QUESTION_DAILY_LIMIT_MESSAGE,
  LIVE_QUESTION_MIN_LENGTH,
  MEDICAL_MATCH_RERUN_AVAILABLE_TOOLTIP,
  MEDICAL_MATCH_RERUN_BLOCKED_TOOLTIP,
  MEDICAL_MATCH_RERUN_IN_PROGRESS_MESSAGE,
} from './constants';

/** Trim and lower once, so every comparison downstream is against one term. */
export const normaliseSearchTerm = (term: string): string => term.trim().toLowerCase();

/**
 * Case-insensitive substring test for the panel's search. Shared by the filter
 * and by the highlighter so the two can't disagree — anything the filter keeps
 * is something the highlighter can then mark up.
 *
 * An empty term matches nothing, so callers can pass one straight through
 * without special-casing the unfiltered panel. Missing text counts as no match
 * rather than throwing: `answer` is typed as a string, but an entry can reach
 * the panel before the worker has written one.
 */
export const matchesSearchTerm = (text: string | null | undefined, term: string): boolean =>
  Boolean(term) && Boolean(text?.toLowerCase().includes(term));

export interface SearchTextSegment {
  text: string;
  isMatch: boolean;
}

/**
 * Break text into alternating plain and matching runs for the highlighter.
 * Walks with `indexOf` rather than a RegExp so a term containing regex
 * metacharacters — `(`, `.`, `+` are all common in medical questions — matches
 * literally instead of throwing or matching the wrong thing. An empty term
 * gives back a single plain run, which is what keeps unfiltered text rendering
 * exactly as it did before the search existed.
 */
export const splitOnSearchTerm = (text: string, term: string): SearchTextSegment[] => {
  if (!term || !text) return [{ text, isMatch: false }];

  const haystack = text.toLowerCase();
  const segments: SearchTextSegment[] = [];
  let cursor = 0;

  for (let at = haystack.indexOf(term); at !== -1; at = haystack.indexOf(term, cursor)) {
    if (at > cursor) segments.push({ text: text.slice(cursor, at), isMatch: false });
    segments.push({ text: text.slice(at, at + term.length), isMatch: true });
    cursor = at + term.length;
  }

  if (cursor < text.length) segments.push({ text: text.slice(cursor), isMatch: false });
  return segments;
};

/**
 * Turn a send-live-questions rejection into a message we're happy to show in a
 * toast. The backend's own `detail` string is deliberately not surfaced for the
 * rate-limit cases — it embeds the raw user id — so each documented status gets
 * a written-for-humans equivalent instead. `question` is what was sent, quoted
 * back when it's the question itself that was turned away.
 */
export const getSendLiveQuestionErrorDetail = (error: unknown, question: string): string => {
  const { status, data } = (error ?? {}) as { status?: number | string; data?: { detail?: unknown } };

  switch (status) {
    case 400:
      return `Your question must be at least ${LIVE_QUESTION_MIN_LENGTH} characters long.`;
    case 403:
      return 'You do not have access to this case.';
    case 404:
      return 'The Medical Match index is unavailable. Run or re-run Medical Match before asking live questions.';
    // Only a full rerun makes 409 mean "busy" here; the live variant is the
    // duplicate-question check wearing a misleading name.
    case 409:
      return String(data?.detail ?? '').includes(LiveMedicalMatchBlockedReason.FULL_MEDMATCH_IN_PROGRESS)
        ? MEDICAL_MATCH_RERUN_IN_PROGRESS_MESSAGE
        : getLiveQuestionDuplicateMessage(question);
    case 422:
      return `Your question must be at least ${LIVE_QUESTION_MIN_LENGTH} characters long and must not duplicate another question.`;
    case 429:
      return LIVE_QUESTION_DAILY_LIMIT_MESSAGE;
    default:
      return 'Your question could not be sent. Please try again.';
  }
};

/**
 * Hover text for the rerun button. The blocked reasons are the interesting
 * case — the button is disabled then, so the tooltip is the only place the
 * user finds out why. `undefined` covers the availability check not having
 * landed yet, which reads the same as "nothing to re-run" for now.
 */
export const getRerunTooltip = (
  canRerun: boolean | undefined,
  reason: MedicalMatchRerunBlockedReason | null | undefined,
): string => {
  if (reason) return MEDICAL_MATCH_RERUN_BLOCKED_TOOLTIP[reason];
  return canRerun ? MEDICAL_MATCH_RERUN_AVAILABLE_TOOLTIP : MEDICAL_MATCH_RERUN_BLOCKED_TOOLTIP.NO_NEW_DOCUMENTS;
};

/**
 * The entries in `sections` that answer `question`: those whose
 * `customQuestionId` points back to it, or that the question lists in
 * `medicalMatchEntryIds` — a question can come back under more than one
 * allegation. Empty while its answer has yet to reach the panel.
 */
export const getAnswerEntryIds = (sections: MedicalMatchItem[], question: MedicalMatchQuestion): string[] =>
  sections
    .flatMap((section) => section.entries)
    .filter(
      ({ entryId, customQuestionId }) =>
        customQuestionId === question.questionId || question.medicalMatchEntryIds.includes(entryId),
    )
    .map(({ entryId }) => entryId);

/**
 * Writes to the clipboard and rejects if that didn't happen. `@repo/utils`'
 * `copyToClipboard` swallows the failure, which leaves a copy button no way to
 * tell the user; `navigator.clipboard` is also missing outside secure contexts,
 * and that has to come back as a rejection rather than a throw.
 */
export const writeToClipboard = async (text: string): Promise<void> => {
  if (!navigator.clipboard) throw new Error('Clipboard access is unavailable');
  await navigator.clipboard.writeText(text);
};

/**
 * Turn a rerun rejection into a toast-ready message. Mirrors
 * `getSendLiveQuestionErrorDetail` — the documented statuses get written-for-
 * humans equivalents rather than the backend's own `detail` string.
 */
export const getRerunErrorDetail = (error: unknown): string => {
  const { status } = (error ?? {}) as { status?: number | string };

  switch (status) {
    case 403:
      return 'You do not have access to this case.';
    case 409:
      return 'Medical Match is already running for this case.';
    case 422:
      return 'Medical Match cannot be re-run yet. Check that all records have finished processing.';
    default:
      return 'Medical Match could not be re-run. Please try again.';
  }
};

interface LiveBlockedReasonParams {
  /** A full rerun is running, which takes the live index away entirely. */
  isRerunning: boolean;
  /** The can-run check is currently erroring. */
  hasCheckError: boolean;
  /** Backend's verdict, or `undefined` while a check is still in flight. */
  liveStatus: CanRunLiveMedicalMatchResponse | undefined;
  /** False while the can-run check has yet to report back for the first time. */
  hasCheckedAvailability: boolean;
}

/**
 * Why the composer is locked, or `undefined` when a question can be asked. The
 * order matters: a rerun outranks everything because it invalidates the live
 * index, and a failed check outranks the backend's verdict because a stale
 * `canRun: true` would let the user fire a question that's certain to bounce.
 */
export const getLiveBlockedReason = ({
  isRerunning,
  hasCheckError,
  liveStatus,
  hasCheckedAvailability,
}: LiveBlockedReasonParams): string | undefined => {
  if (isRerunning) return MEDICAL_MATCH_RERUN_IN_PROGRESS_MESSAGE;
  if (hasCheckError) return LIVE_MEDICAL_MATCH_CHECK_FAILED_MESSAGE;
  if (!hasCheckedAvailability || !liveStatus) return LIVE_MEDICAL_MATCH_CHECKING_MESSAGE;
  if (liveStatus.canRun) return undefined;
  // Despite the name, this reason means a *similar question* is already being
  // answered — not that the case is busy. It says nothing about the question
  // the user is about to type, so it must not lock the composer; a genuine
  // duplicate is caught by the 409 on send instead.
  if (liveStatus.reason === LiveMedicalMatchBlockedReason.LIVE_MEDMATCH_IN_PROGRESS) return undefined;
  return liveStatus.reason
    ? LIVE_MEDICAL_MATCH_BLOCKED_MESSAGE[liveStatus.reason]
    : LIVE_MEDICAL_MATCH_UNAVAILABLE_MESSAGE;
};
